import React, { useState, useEffect } from 'react';
import { Search, Filter, Car, MapPin, Clock, Calendar, Truck } from 'lucide-react';
import { useCity } from '../../contexts/CityContext';
import { useDateFilter } from '../../contexts/DateFilterContext';
import config from '../../config';
import './AssignmentReference.css';

const AssignmentReference = () => {
  const { selectedCity } = useCity();
  const { isDateInRange } = useDateFilter();
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  // Fetch bookings and assignment data from API
  useEffect(() => {
    const fetchBookings = async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) setLoading(true);
        
        // Fetch both bookings and assignments in parallel
        const [bookingsResponse, assignmentsResponse] = await Promise.all([
          fetch(`${config.dispatchApiBase}/bookings`),
          fetch(`${config.dispatchApiBase}/assignment-states`)
        ]);
        
        const bookingsData = await bookingsResponse.json();
        const assignmentsData = await assignmentsResponse.json();
        
        // Check if both APIs returned successful data
        if (bookingsData.success && bookingsData.data && Array.isArray(bookingsData.data)) {
          // Create a map of booking_id to assignment state data
          const assignmentStateMap = new Map();
          if (assignmentsData.success && assignmentsData.data && Array.isArray(assignmentsData.data)) {
            assignmentsData.data.forEach(assignment => {
              assignmentStateMap.set(assignment.booking_id, {
                vehicle_id: assignment.vehicle_id,
                state: assignment.state || 'unassigned'
              });
            });
          }
          
          // Transform the data to match our component structure
          const transformedBookings = bookingsData.data.map(booking => {
            const assignmentState = assignmentStateMap.get(booking.id);
            return {
              booking_id: booking.id,
              type: booking.vehicle_type || 'class2',
              distance: booking.distance || 0,
              pickup_time: booking.pickup_time,
              vehicle_id: assignmentState?.vehicle_id || null,
              vehicle_type: booking.vehicle_type || 'class2',
              state: assignmentState?.state || 'unassigned',
              pickup_location: booking.pickup_location || 'Unknown Location',
              drop_location: booking.drop_location || 'Unknown Location',
              estimated_duration: Math.round(booking.duration || 0),
              fare: booking.payment_estimate || 0
            };
          });
          
          setAssignments(transformedBookings);
          setFilteredAssignments(transformedBookings);
        } else {
          console.error('Failed to fetch bookings:', bookingsData.error || 'Invalid data format');
          setAssignments([]);
          setFilteredAssignments([]);
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setAssignments([]);
        setFilteredAssignments([]);
      } finally {
        if (isInitialLoad) setLoading(false);
      }
    };

    fetchBookings(true); // Initial load
    
    // Set up polling for real-time updates every 5 seconds
    const interval = setInterval(fetchBookings, 5000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let filtered = assignments;

    // Filter by city first if a city is selected (and not "All")
    if (selectedCity && selectedCity !== 'All') {
      filtered = filtered.filter(assignment =>
        assignment.pickup_location.toLowerCase().includes(selectedCity.toLowerCase()) ||
        assignment.drop_location.toLowerCase().includes(selectedCity.toLowerCase())
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(assignment =>
        assignment.booking_id.toString().includes(searchTerm) ||
        assignment.vehicle_id.toString().includes(searchTerm) ||
        assignment.pickup_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assignment.drop_location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(assignment => assignment.type === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(assignment => assignment.state === filterStatus);
    }

    // Apply date filter on pickup_time
    filtered = filtered.filter(assignment => {
      const assignmentDate = assignment.pickup_time;
      return !assignmentDate || isDateInRange(assignmentDate);
    });

    setFilteredAssignments(filtered);
  }, [assignments, searchTerm, filterType, filterStatus, selectedCity, isDateInRange]);

  const getVehicleColor = (vehicleType) => {
    const colors = {
      'class1': '#ef4444',
      'class2': '#3b82f6',
      'class3': '#10b981',
      'class4': '#f59e0b',
      'class5': '#8b5cf6',
      'class6': '#06b6d4',
      'class7': '#f97316',
      'class8': '#84cc16',
      'class9': '#ec4899'
    };
    return colors[vehicleType] || '#6b7280';
  };

  const getStateColor = (state) => {
    const colors = {
      'assigned': '#10b981',
      'unassigned': '#f59e0b',
      'completed': '#3b82f6',
      'cancelled': '#ef4444',
      'in_progress': '#8b5cf6'
    };
    return colors[state.toLowerCase()] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading assignment reference...</p>
      </div>
    );
  }

  return (
    <div className="assignment-reference">
      <div className="page-header">
        <h1>Assignment Reference</h1>
        <p>Comprehensive booking assignments overview for today</p>
      </div>

      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-info">
            <h3>{assignments.length}</h3>
            <p>Total Assignments</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Truck size={24} />
          </div>
          <div className="stat-info">
            <h3>{assignments.filter(a => a.state === 'assigned').length}</h3>
            <p>Assigned</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <MapPin size={24} />
          </div>
          <div className="stat-info">
            <h3>{assignments.reduce((sum, a) => sum + a.distance, 0).toFixed(1)} km</h3>
            <p>Total Distance</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Car size={24} />
          </div>
          <div className="stat-info">
            <h3>{new Set(assignments.map(a => a.vehicle_id)).size}</h3>
            <p>Active Vehicles</p>
          </div>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by booking ID, vehicle ID, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <div className="filter-box">
            <Filter size={20} />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="class1">Class 1</option>
              <option value="class2">Class 2</option>
              <option value="class3">Class 3</option>
              <option value="class4">Class 4</option>
              <option value="class5">Class 5</option>
              <option value="class6">Class 6</option>
              <option value="class7">Class 7</option>
            </select>
          </div>

          <div className="filter-box">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>
        </div>
      </div>

      <div className="assignments-table-container">
        <table className="assignments-table">
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Status</th>
              <th>Vehicle</th>
              <th>Pickup Location</th>
              <th>Drop Location</th>
              <th>Pickup Time</th>
              <th>Distance</th>
              <th>Fare</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAssignments.map(assignment => (
              <React.Fragment key={assignment.booking_id}>
                <tr className={`assignment-row ${assignment.state.toLowerCase()}`}>
                  <td className="booking-id-cell">#{assignment.booking_id}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: `${getStateColor(assignment.state)}20`, color: getStateColor(assignment.state) }}
                    >
                      {assignment.state.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="vehicle-badge-cell" style={{ color: getVehicleColor(assignment.vehicle_type) }}>
                      <Car size={16} />
                      <span>{assignment.vehicle_id ? `V-${assignment.vehicle_id}` : 'Unassigned'}</span>
                      <span className="vehicle-class">({assignment.type})</span>
                    </div>
                  </td>
                  <td className="location-cell">
                    <span className="location-icon">📍</span>
                    {assignment.pickup_location}
                  </td>
                  <td className="location-cell">
                    <span className="location-icon">🎯</span>
                    {assignment.drop_location}
                  </td>
                  <td>
                    <div className="time-cell">
                      <Clock size={14} />
                      <span>{new Date(assignment.pickup_time).toLocaleTimeString()}</span>
                    </div>
                  </td>
                  <td>{assignment.distance} km</td>
                  <td className="fare-cell">Rs {assignment.fare.toFixed(2)}</td>
                  <td className="actions-cell">
                    <button 
                      className="btn-view-details"
                      onClick={() => {
                        const row = document.getElementById(`details-${assignment.booking_id}`);
                        row.style.display = row.style.display === 'none' ? 'table-row' : 'none';
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
                <tr id={`details-${assignment.booking_id}`} className="details-row" style={{display: 'none'}}>
                  <td colSpan="9">
                    <div className="details-container">
                      <div className="details-content">
                        <div className="detail-section">
                          <h4>📋 Booking Information</h4>
                          <p><strong>Booking ID:</strong> #{assignment.booking_id}</p>
                          <p><strong>Vehicle Type:</strong> {assignment.type}</p>
                          <p><strong>Status:</strong> {assignment.state.toUpperCase()}</p>
                          <p><strong>Estimated Duration:</strong> {assignment.estimated_duration} min</p>
                          <p><strong>Fare:</strong> Rs {assignment.fare.toFixed(2)}</p>
                        </div>
                        <div className="detail-section">
                          <h4>🚗 Vehicle Details</h4>
                          <p><strong>Vehicle ID:</strong> {assignment.vehicle_id ? `Vehicle ${assignment.vehicle_id}` : 'No Vehicle Assigned'}</p>
                          <p><strong>Vehicle Class:</strong> {assignment.vehicle_type}</p>
                          <p><strong>Distance:</strong> {assignment.distance} km</p>
                        </div>
                        <div className="detail-section">
                          <h4>📍 Route Information</h4>
                          <p><strong>Pickup:</strong> {assignment.pickup_location}</p>
                          <p><strong>Pickup Time:</strong> {new Date(assignment.pickup_time).toLocaleString()}</p>
                          <p><strong>Drop:</strong> {assignment.drop_location}</p>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAssignments.length === 0 && (
        <div className="no-results">
          <Search size={48} />
          <h3>No assignments found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default AssignmentReference;